// One date format vocabulary for the whole app (ADR 0026). Local time, the
// day first, a four-digit year, 24-hour clock.

export const DATE_TIME_FORMATS = [
  'DD-MM-YYYY',
  'DD-MM-YYYY HH:mm:ss',
  'DD-MM-YYYY HH:mm:ss.SSS',
  'MM-YYYY'
] as const

export type DateTimeFormat = (typeof DATE_TIME_FORMATS)[number]

/** What a date may arrive as: an ISO string, epoch milliseconds or a Date. */
export type DateInput = string | number | Date | null | undefined

/** `2026-10-16` or `2026-10`: a calendar date or month, not an instant. */
const CALENDAR = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/

/**
 * The value as a Date, or `null` when it is empty or not a date. A bare
 * `YYYY-MM-DD` or `YYYY-MM` is read as a local calendar date, not as UTC
 * midnight, so it never shows as the day before west of Greenwich.
 */
export function parseDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'string') {
    const calendar = CALENDAR.exec(value.trim())
    if (calendar) {
      const [, year, month, day = '01'] = calendar
      const date = new Date(Number(year), Number(month) - 1, Number(day))
      return Number.isNaN(date.getTime()) ? null : date
    }
  }
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0')

/** The two parts a format shows: the calendar part and, if any, the clock. */
export interface DateTimeParts {
  date: string
  time?: string
}

export function dateTimeParts(date: Date, format: DateTimeFormat): DateTimeParts {
  const day = `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${String(date.getFullYear())}`
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  switch (format) {
    case 'MM-YYYY':
      return { date: `${pad(date.getMonth() + 1)}-${String(date.getFullYear())}` }
    case 'DD-MM-YYYY':
      return { date: day }
    case 'DD-MM-YYYY HH:mm:ss':
      return { date: day, time: clock }
    case 'DD-MM-YYYY HH:mm:ss.SSS':
      return { date: day, time: `${clock}.${pad(date.getMilliseconds(), 3)}` }
  }
}

/** The value in `format`, or `''` when it is not a date. */
export function formatDateTime(value: unknown, format: DateTimeFormat): string {
  const date = parseDate(value)
  if (!date) return ''
  const { date: day, time } = dateTimeParts(date, format)
  return time === undefined ? day : `${day} ${time}`
}

/** The machine-readable `<time datetime>` value for `format`. */
export function machineDateTime(value: unknown, date: Date, format: DateTimeFormat): string {
  if (format === 'MM-YYYY') return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}`
  if (format === 'DD-MM-YYYY') {
    return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }
  return typeof value === 'string' ? value : date.toISOString()
}
